// src/components/assets/AssetForm.tsx
import React from 'react';
import { Save, X, RefreshCw } from 'lucide-react';
import { AssetData } from '../../pages/Assets'; // Adjust path if necessary
import { LookupValue } from '../../services/lookupService'; // Adjust path
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface AssetFormProps {
  formData: AssetData;
  editingId: string | null;
  isFormSubmitted: boolean;
  dropdownOptions: {
    ownerType: LookupValue[];
    assetType: LookupValue[];
    assetSubType: LookupValue[];
  };
  dependents: { id: string; name: string }[];
  loadingSubTypes: boolean;
  handleSubmit: (e: React.FormEvent) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onCancel: () => void;
}

const renderDropdownOptions = (options: LookupValue[], placeholder = 'Select...') => {
    if (options.length === 0) return <option value="">No options available</option>;
    return (
      <>
        <option value="">{placeholder}</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </>
    );
};

const AssetForm: React.FC<AssetFormProps> = ({
  formData,
  editingId,
  isFormSubmitted,
  dropdownOptions,
  dependents,
  loadingSubTypes,
  handleSubmit,
  handleInputChange,
  handleSelectChange,
  onCancel,
}) => {
  return (
    <div className="p-6 bg-white">
      <h3 className="text-xl font-semibold text-gray-900 mb-4 md:mb-6">
        {editingId ? 'Edit Asset' : 'Add New Asset'}
      </h3>
      <form onSubmit={handleSubmit}>
        {/* Form Fields Grid */}
        <div className="bg-white grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-4">
            {/* Asset Owner Type */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Asset Owner Type *</label>
                <select name="assetOwnerType" value={formData.assetOwnerType} onChange={handleSelectChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required>
                    {renderDropdownOptions(dropdownOptions.ownerType, 'Select Owner Type')}
                </select>
            </div>
            {/* Family Member (Conditional) */}
            {formData.assetOwnerType === '2' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Family Member *</label>
                  <select name="familyMemberId" value={formData.familyMemberId} onChange={handleSelectChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required>
                    <option value="">Select Family Member</option>
                    {dependents.map((dep) => (
                      <option key={dep.id} value={dep.id}>{dep.name}</option>
                    ))}
                  </select>
                </div>
            )}
            {/* Other form fields... */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Asset Name *</label>
                <input type="text" name="assetName" value={formData.assetName} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Asset Type *</label>
                <select name="assetType" value={formData.assetType} onChange={handleSelectChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required>
                    {renderDropdownOptions(dropdownOptions.assetType, 'Select Type')}
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Asset Sub Type *</label>
                <select name="assetSubType" value={formData.assetSubType} onChange={handleSelectChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required disabled={loadingSubTypes}>
                    {loadingSubTypes ? <option>Loading...</option> : renderDropdownOptions(dropdownOptions.assetSubType, formData.assetType ? 'Select Sub Type' : 'Select Type first')}
                </select>
            </div>
            {/* ... continue for all other fields */}
             <div className="md:col-span-2 lg:col-span-1">
               <label className="block text-sm font-medium text-gray-700 mb-2">
                Purchase Date
            </label>
            <DatePicker
                selected={formData.assetspurchaseDate ? new Date(formData.assetspurchaseDate) : null}
                onChange={(date: Date | null) => {
                handleInputChange({
                    target: {
                    name: 'assetspurchaseDate',
                    value: date ? date.toISOString().split('T')[0] : ''
                    }
                } as React.ChangeEvent<HTMLInputElement>);
                }}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select purchase date"
                className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={15}
                maxDate={new Date()} // Prevents future dates
                popperClassName="z-[100]"
                wrapperClassName="w-full"
            />
            </div>
              <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Value</label>
                <input type="number" name="assetspurchaseValue" value={formData.assetspurchaseValue} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Unit</label>
                <input type="number" name="assetUnit" value={formData.assetUnit} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Unit Price</label>
                <input type="number" name="assetUnitPrice" value={formData.assetUnitPrice} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Price *</label>
                <input type="number" name="assetCurrentPrice" value={formData.assetCurrentPrice} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" required />
              </div>
              <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Post Code</label>
                <input type="text" name="assetPostCode" value={formData.assetPostCode} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div className="md:col-span-2 lg:col-span-1 flex items-center pt-6">
                <label className="block text-sm font-medium text-gray-700">Asset on Loan</label>
                <input type="checkbox" name="assetLoan" checked={formData.assetLoan} onChange={handleInputChange} className="h-5 w-5 ml-2 text-green-600 focus:ring-green-500 border-gray-300 rounded" />
              </div>
        </div>

       
        <div className="flex flex-col-reverse md:flex-row justify-end space-y-reverse space-y-2 md:space-y-0 md:space-x-4 mt-6">
          <button type="button" onClick={onCancel} className="bg-red-500 text-white py-2 px-4 md:px-6 md:py-3 rounded-lg hover:bg-red-600 flex items-center justify-center">
            <X className="w-4 h-4 mr-2" />Cancel
          </button>
          <button type="submit" className="px-4 py-2 md:px-6 md:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center" disabled={isFormSubmitted}>
            {isFormSubmitted ? <RefreshCw className="w-4 h-4 md:w-5 md:h-5 mr-2 animate-spin" /> : <Save className="w-4 h-4 md:w-5 md:h-5 mr-2" />}
            {isFormSubmitted ? 'Loading' : editingId ? 'Update Asset' : 'Add Asset'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AssetForm;